import { supabase } from "@/lib/supabase";

/** Cap session lookup so a hung navigator.locks getSession cannot block the XHR. */
export const SESSION_LOOKUP_BUDGET_MS = 2000;
export const SESSION_SLOW_MS = 500;

export type SessionLookup = {
  token: string | null;
  elapsedMs: number;
  timedOut: boolean;
  failed: boolean;
};

type LookupContext = {
  url?: string;
  method?: string;
};

/**
 * Read the Supabase access token for an outgoing API call.
 * On timeout or failure, fall back to the in-memory token already set by the caller.
 * Never logs token material.
 */
export async function resolveRequestAccessToken(
  cachedToken: string | null,
  context: LookupContext = {},
): Promise<SessionLookup> {
  const started = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const logContext = {
    url: context.url,
    method: context.method,
    hasCachedToken: Boolean(cachedToken),
  };

  try {
    // Attach a rejection handler so a late getSession failure is not unhandled
    // after the budget already won the race.
    const sessionPromise = supabase.auth.getSession().then(
      (value) => ({ kind: "session" as const, value }),
      (error: unknown) => ({ kind: "error" as const, error }),
    );
    const timeoutPromise = new Promise<{ kind: "timeout" }>((resolve) => {
      timer = setTimeout(
        () => resolve({ kind: "timeout" }),
        SESSION_LOOKUP_BUDGET_MS,
      );
      // Don't keep a Node test worker alive if this timer outlives the request.
      const nodeTimer = timer as { unref?: () => void };
      nodeTimer.unref?.();
    });
    const result = await Promise.race([sessionPromise, timeoutPromise]);
    const elapsedMs = Date.now() - started;

    if (result.kind === "timeout") {
      console.warn(
        `[api] supabase.auth.getSession exceeded ${SESSION_LOOKUP_BUDGET_MS}ms (${elapsedMs}ms); using cached access token`,
        logContext,
      );
      return { token: cachedToken, elapsedMs, timedOut: true, failed: false };
    }

    if (result.kind === "error") {
      const message =
        result.error instanceof Error ? result.error.message : "unknown";
      console.warn(
        `[api] supabase.auth.getSession failed after ${elapsedMs}ms; using cached access token`,
        { ...logContext, message },
      );
      return { token: cachedToken, elapsedMs, timedOut: false, failed: true };
    }

    if (elapsedMs >= SESSION_SLOW_MS) {
      console.warn(
        `[api] supabase.auth.getSession slow: ${elapsedMs}ms`,
        logContext,
      );
    }

    const accessToken = result.value.data?.session?.access_token;
    return {
      token: accessToken || cachedToken,
      elapsedMs,
      timedOut: false,
      failed: false,
    };
  } catch (error) {
    const elapsedMs = Date.now() - started;
    const message = error instanceof Error ? error.message : "unknown";
    console.warn(
      `[api] supabase.auth.getSession failed after ${elapsedMs}ms; using cached access token`,
      { ...logContext, message },
    );
    return { token: cachedToken, elapsedMs, timedOut: false, failed: true };
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}
